import React, { useState, useRef, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import HeaderTop from './HeaderTop';
import HeaderBottom from './HeaderBottom';
import Footer from './Footer';
import Sidebar from '../../../core/sidebar/Sidebar';
import NavigationLeft from '../../../core/menu/NavigationLeft';
import NavigationRight from '../../../core/menu/NavigationRight';
import MobileMenu from '../../../core/menu/MobileMenu';
import ThemeRemoteControl from '../../../core/theme/ThemeRemoteControl';
import RelatedSitesCarousel from '../../../core/carousel/RelatedSitesCarousel';
import { useTheme } from '../../../assets/contexts/ThemeContext';
import { usePageLayout } from '../../../assets/contexts/PageLayoutContext';
import PageLoadingSkeleton from '../pages/comm/PageLoadingSkeleton';
import { useMegaMenu } from '../../../assets/hooks/useMegaMenu';
import { useMenuScroll } from '../../../assets/hooks/useMenuScroll';
import useSidebarMenu from '../../../assets/hooks/useSidebarMenu';
import BannerContainer from '../../../core/title-banner/BannerContainer';
import BannerSkeleton from '../../../components/skeleton/BannerSkeleton';
import useMenuData from '../../../assets/hooks/useMenuData';
import { useNavLayoutClass } from '../../../assets/utils/comm/useNavLayoutClass';
import { useNavOffsets } from '../../../assets/utils/comm/useNavOffsets';
import { useAOSRefresh } from '../../../assets/utils/comm/useAOSRefresh';
import { useMobileMenuOverride } from '../../../assets/utils/comm/useMobileMenuOverride';
import { useMegaMenuListeners } from '../../../assets/utils/comm/useMegaMenuListeners';
import useRelatedSites from '../../../assets/hooks/useRelatedSites';
import { shouldSkipMenuFetch } from '../../../assets/utils/menu/menuFetchPolicy';

const MainLayout = () => {
  const location = useLocation();
  const [showSidebar, setShowSidebar] = useState(false);
  const themeContext = useTheme();
  // themeContext의 themeConfig를 우선 사용 (실시간 변경 반영)
  const themeConfig = themeContext?.themeConfig || (typeof window !== 'undefined' && window.__themeConfig) || {};
  const navLayout = themeConfig.navLayout || 'top';
  const sidebarMenuType = themeConfig.sidebarMenuType || 'accordion';
  const mainMenuRef = useRef(null);
  const mainContentRef = useRef(null); // 업무화면 영역 ref (AOS refresh를 위해)

  // 경로 변경 시 테마 재로드
  useEffect(() => {
    const loadThemeForCurrentPath = async () => {
      if (typeof window !== 'undefined' && window.ThemeConfig) {
        try {
          await window.ThemeConfig.loadThemeForPath(location.pathname);
        } catch (e) {
          console.error('[MainLayout] 경로별 테마 로드 실패:', e);
        }
      }
    };

    loadThemeForCurrentPath();
  }, [location.pathname]);

  // 연관 사이트 데이터 로드
  const relatedSitesData = useRelatedSites();

  // 커스텀 훅 사용
  // alliance-list는 좌측 사이드바+필터 사용으로 메뉴 로드, 그 외 /alliance는 자체 레이아웃
  const isAlliancePath = location.pathname.startsWith('/alliance') && !location.pathname.includes('alliance-list');
  const shouldSkipMenu = shouldSkipMenuFetch({ pathname: location.pathname, isAlliancePath });
  const { menuData } = useMenuData(shouldSkipMenu ? { skip: true } : {});
  const { sidebarMenu } = useSidebarMenu(menuData, location.pathname);
  const sidebarLoading = menuData.length === 0; // 메뉴 데이터가 없으면 로딩 중
  const { bannerData, sidebarVisible, contentLoading, setMenuDataForBanner, setBannerPathname } = usePageLayout();

  useEffect(() => {
    setBannerPathname(location.pathname);
  }, [location.pathname, setBannerPathname]);

  useEffect(() => {
    setMenuDataForBanner(menuData);
  }, [menuData, setMenuDataForBanner]);

  // 레이아웃 관련 훅
  useNavLayoutClass(navLayout);
  useNavOffsets(navLayout);

  // 메가메뉴 훅 사용 (상단 배치이고 하위 메뉴 타입이 메가메뉴일 때만)
  const submenuType = themeConfig.submenuType || 'mega';
  useMegaMenu(navLayout === 'top' && submenuType !== 'dropdown' ? menuData : null, mainMenuRef);

  // 메뉴 스크롤 훅 사용 (상단 배치일 때만, fixedWithMore일 때는 wrap으로 적용)
  const menuScrollType = themeConfig.menuScrollType || 'wrap';
  useMenuScroll(
    navLayout === 'top' ? menuScrollType : null,
    mainMenuRef,
    menuData,
    themeConfig.menuAlign || 'left'
  );

  // AOS 및 플러그인 초기화 훅
  useAOSRefresh(location.pathname, mainContentRef);
  useMobileMenuOverride();
  useMegaMenuListeners();

  const toggleSidebar = () => {
    setShowSidebar(!showSidebar);
  };

  return (
    <>
      {/* 헤더 상단 영역 (항상 표시, 최상단에 배치) */}
      <header className="header-area header-responsive-padding header-height-1">
        <HeaderTop />
        {/* 헤더 하단 영역 (상단 배치일 때만 표시) */}
        {navLayout === 'top' && (
          <HeaderBottom ref={mainMenuRef} menuData={menuData} onMenuClick={toggleSidebar} />
        )}
      </header>

      <div className={`main-wrapper main-wrapper-2 ${showSidebar ? 'overlay-active-2' : ''}`} style={{ overflowX: 'hidden' }}>
        {/* Overlay 배경 - 원본과 동일하게 main-wrapper-2의 자식으로 배치 */}
        <div
          className="body-overlay-2"
          onClick={() => setShowSidebar(false)}
        ></div>

        {/* 좌측 네비게이션 (header 아래에 배치) */}
        {/* /alliance 경로에서는 AllianceSidebar를 사용하므로 NavigationLeft를 렌더링하지 않음 */}
        {navLayout === 'left' && !location.pathname.startsWith('/alliance') && (
          <NavigationLeft menuData={menuData} />
        )}

        {/* 우측 네비게이션 (header 아래에 배치) */}
        {navLayout === 'right' && <NavigationRight menuData={menuData} />}

        {/* 제목배너 영역 (업무화면과 형제) */}
        {bannerData && (
          bannerData.loading ? (
            <BannerSkeleton />
          ) : bannerData.title ? (
            <BannerContainer
              title={bannerData.title}
              breadcrumbPath={bannerData.breadcrumbPath || null}
            />
          ) : null
        )}

        {/* 사이드바와 업무화면 영역 (형제 관계) */}
        {/* alliance-list는 좌측 사이드바+필터 레이아웃 사용, 그 외 /alliance는 Outlet만 */}
        {location.pathname.startsWith('/alliance') && !location.pathname.includes('alliance-list') ? (
          <div className="main-content-wrapper" style={{ position: 'relative', minHeight: '60vh' }}>
            {contentLoading && (
              <div className="main-content-skeleton-overlay" style={{ position: 'absolute', inset: 0, zIndex: 1, background: '#fff' }}>
                <PageLoadingSkeleton variant="cardList" showTabs={false} />
              </div>
            )}
            <div style={{ visibility: contentLoading ? 'hidden' : 'visible' }}><Outlet /></div>
          </div>
        ) : (
        <div className="container my-4" style={{ minHeight: '60vh' }}>
          <div className="row g-4">
            {/* 좌측 사이드바 - sidebarMenu가 존재하고 enabled !== false일 때만 표시 */}
            {sidebarMenuType !== 'none' &&
             sidebarMenu &&
             sidebarMenu.enabled !== false &&
             sidebarVisible &&
             (sidebarMenu.position === 'left' || !sidebarMenu.position) && (
              <Sidebar
                sidebarMenu={sidebarMenu}
                sidebarLoading={sidebarLoading}
              />
            )}

            {/* 업무화면 영역 (동적 컬럼 클래스 - 사이드바 유무에 따라 변경) */}
            <div className={(
              sidebarMenuType !== 'none' &&
              sidebarMenu &&
              sidebarMenu.enabled !== false &&
              sidebarVisible
            ) ? 'col-12 col-lg-9' : 'col-12'}>
              <div className="main-content" ref={mainContentRef} style={{ position: 'relative' }}>
                {contentLoading && (
                  <div className="main-content-skeleton-overlay" style={{ position: 'absolute', inset: 0, zIndex: 1, background: '#fff' }}>
                    <PageLoadingSkeleton variant="cardList" showTabs={false} />
                  </div>
                )}
                <div style={{ visibility: contentLoading ? 'hidden' : 'visible' }}>
                  <Outlet />
                </div>
              </div>
            </div>

            {/* 우측 사이드바 - sidebarMenu가 존재하고 enabled !== false일 때만 표시 */}
            {sidebarMenuType !== 'none' &&
             sidebarMenu &&
             sidebarMenu.enabled !== false &&
             sidebarVisible &&
             sidebarMenu.position === 'right' && (
              <Sidebar
                sidebarMenu={sidebarMenu}
                sidebarLoading={sidebarLoading}
              />
            )}
          </div>
        </div>
        )}

        {/* 연관 사이트 캐러셀 (업무화면과 푸터 사이) */}
        {relatedSitesData && relatedSitesData.sites && relatedSitesData.sites.length > 0 && (
          <div className="related-sites-section">
            <div className="container">
              <RelatedSitesCarousel data={relatedSitesData} />
            </div>
          </div>
        )}
      </div>

      {/* 푸터 영역 (nav-layout보다 하단에 전체 너비로 배치) */}
      {themeConfig.footerEnabled !== false && (
      <Footer className={`footer-theme-${(typeof window !== 'undefined' && window.__themeConfig ? window.__themeConfig.footerTheme : null) || themeConfig.footerTheme || 'simple'}`} />
      )}

      {/* 모바일 메뉴 (off-canvas) */}
      <MobileMenu show={showSidebar} onClose={() => setShowSidebar(false)} menuData={menuData} />

      {/* 우측 사이드바 공통(장바구니·알림)은 App.js에서 RightSidebarProvider와 함께 한 번만 렌더 */}

      {/* 테마 원격 제어기 */}
      <ThemeRemoteControl />

    </>
  );
};

export default MainLayout;
